import React from 'react';
import { AlertTriangle, AlertOctagon, ShieldCheck } from 'lucide-react';

const RiskAssessmentSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  riskLevels,
  aiSectors 
}) => {
  // Иконки для уровней риска
  const riskIcons = {
    'low': ShieldCheck,
    'moderate': AlertTriangle,
    'high': AlertOctagon
  };

  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <AlertTriangle className="w-5 h-5 mr-2 text-indigo-500" />
        Оценка рисков и секторов ИИ
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Уровень риска */}
        <div className="col-span-full">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Уровень инвестиционного риска
          </div>
          <div className="grid grid-cols-3 gap-2">
            {riskLevels.map((level) => {
              const RiskIcon = riskIcons[level.id];
              return (
                <label 
                  key={level.id} 
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex flex-col items-center text-center
                    ${formData.riskLevel === level.id 
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  <input
                    type="radio"
                    name="riskLevel"
                    value={level.id}
                    checked={formData.riskLevel === level.id}
                    onChange={handleInputChange}
                    className="hidden"
                  />
                  <RiskIcon className={`w-6 h-6 mb-2 ${formData.riskLevel === level.id ? 'text-indigo-600' : 'text-gray-500'}`} />
                  <span className="text-sm font-medium">{level.name}</span>
                  <span className="text-xs opacity-70 mt-1">{level.description}</span>
                </label>
              );
            })}
          </div>
          {errors.riskLevel && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.riskLevel}
            </p>
          )}
        </div>

        {/* Секторы ИИ */}
        <div className="col-span-full">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Секторы ИИ для инвестирования
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {aiSectors && aiSectors.map((sector) => (
              <label 
                key={sector.id} 
                className={`p-3 rounded-lg border cursor-pointer transition-colors flex flex-col 
                  ${formData.aiSectors.includes(sector.id)
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <input
                  type="checkbox"
                  name="aiSectors"
                  value={sector.id}
                  checked={formData.aiSectors.includes(sector.id)}
                  onChange={(e) => {
                    const currentSectors = formData.aiSectors;
                    const newSectors = e.target.checked 
                      ? [...currentSectors, sector.id]
                      : currentSectors.filter(s => s !== sector.id);
                    
                    handleInputChange({
                      target: {
                        name: 'aiSectors',
                        value: newSectors
                      }
                    });
                  }}
                  className="hidden"
                />
                <span className={`text-sm font-medium ${formData.aiSectors.includes(sector.id) ? 'text-indigo-600' : 'text-gray-700 dark:text-gray-300'}`}>
                  {sector.name}
                </span>
                <span className="text-xs opacity-70 mt-1">{sector.description}</span>
              </label>
            ))}
          </div>
          {errors.aiSectors && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.aiSectors}
            </p>
          )}
        </div>
      </div>

      {/* Информационный блок о рисках */}
      <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-100 dark:border-yellow-800">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
              Важно понимать риски
            </h4>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              Инвестиции в технологии ИИ могут быть высокорисковыми. 
              Низкий риск подразумевает консервативную стратегию с 
              ETF и крупными компаниями, высокий риск - инвестиции 
              в перспективные, но менее стабильные стартапы.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAssessmentSection;